<script lang="ts" setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast, POSITION } from 'vue-toastification';
import { lostPassword } from '@/api/user';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import messages from '@/locales/account/messages';

const { t, locale } = useI18n(
    {
        useScope: 'global',
        messages: messages
    }
)
const toast = useToast()
const form = ref({
    email: ''
})
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const validEmail = helpers.regex(new RegExp(emailRegex.source, emailRegex.flags));
const v$ = useVuelidate({
        email: { required, validEmail }
    }, form)

function submit(){
    v$.value.$touch()
    if(v$.value.$error){
        toast.error(t('account.lost.messages.form_error'), {
            position: POSITION.TOP_CENTER
        })
        return
    }
    lostPassword(form.value.email).then((res)=>{
        toast.success(t('account.lost.messages.success'), {
            position: POSITION.TOP_CENTER
        })
        form.value.email = ''
    }).catch((err)=>{
        toast.error(t('account.lost.messages.error'), {
            position: POSITION.TOP_CENTER
        })
    })
}
</script>
<template>
    <div class="account">
        <div class="account-nav">
            <h1>{{$t("account.menu.title")}}</h1>
            <nav>
                <router-link :to="{name: locale+'.account.login'}">{{$t("account.menu.connect")}}</router-link>
                <router-link :to="{name: locale+'.account.lost'}">{{$t("account.menu.lost")}}</router-link>
            </nav>
        </div>
        <div class="content">
            <h2>{{$t("account.lost.title")}}</h2>
            <p>{{$t("account.lost.description")}}</p>
            <form @submit.prevent="submit">
                <div class="form-group
                ">
                    <label for="email">{{$t("account.lost.email")}}</label>
                    <input type="text" id="email" v-model="v$.email.$model" @blur="v$.email.$touch" :class="[{error:(v$.email.$dirty && v$.email.$error)},{valid:(v$.email.$dirty && !v$.email.$error)}]"  >
                    <button type="submit">{{$t("account.lost.receive")}}</button>
                </div>
            </form>
            <p>{{$t("account.lost.explain")}}</p>
        </div>
    </div>
</template>
<style lang="scss" scoped>
.account{
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    .account-nav{
        width: 100%;
        position: relative;
        color: #FFF;
        &:before{
            content:'';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(257.47deg, #3C5A6B -8.78%, #0E2E5B 110.74%);
            z-index: -1;
        }
        h1{
            font-family: Montserrat;
            font-weight: 700;
            font-size: 30px;
            line-height: 36.57px;
            letter-spacing: 0%;
            text-align: center;
            margin: 20px 0;
        }
        nav{
            display: flex;
            padding-left:20px;
            padding-right: 20px;
            justify-content: space-around;
            a{
                display: block;
                color: #FFF;
                text-decoration: none;
                font-family: Montserrat;
                font-weight: 600;
                font-size: 14px;
                line-height: 17.07px;
                letter-spacing: 0%;

                text-align: center;
                padding: 10px 10px 5px 10px;
                transition: all 0.3s;
                border-bottom: 4px solid transparent;
                &:hover{
                    color: #F52B2B;
                    border-bottom: 4px solid #F52B2B;
                }
                &.router-link-active{
                    border-bottom: 4px solid #F52B2B;
                }
            }
        }
    }
    .content{
        padding: 20px;
        h2{
            font-family: Montserrat;
            font-weight: 500;
            font-size: 24px;
            line-height: 29.26px;
            letter-spacing: 0%;
        }
        p{
            font-family: Montserrat;
            font-weight: 400;
            font-size: 14px;
            line-height: 17.07px;
            letter-spacing: 0%;
            margin: 20px 0;
        }
        form{
            .form-group{
                label{
                    display: block;
                    width: 100%;
                    margin-bottom: 5px;
                    padding-left: 10px;
                    margin-top: 10px;
                    font-family: Montserrat;
                    font-weight: 600;
                    font-size: 14px;
                    line-height: 17.07px;
                    letter-spacing: 0%;
                }
                input[type="text"], input[type="password"], .input{
                    width: 100%;
                    padding-left: 10px;
                    padding-right: 10px;
                    border-radius: 19px;
                    border: none;
                    background-color: #FFF;
                    font-size: 14px;
                    line-height: 32px;
                    box-shadow: 0px 0px 4px 0px #00000040 inset;
                    &.error{
                        box-shadow: 0px 0px 4px 0px #FF0000 inset;
                    }
                    &.valid{
                        box-shadow: 0px 0px 4px 0px #00a914 inset;
                    }
                    &[readonly]{
                        background-color: #F0F0F0;
                    }
                    &:deep(&){
                        #input-2-messages, .v-input__details{
                            display: none;
                        }
                        .v-field__input{
                            padding-top: 0;
                        }
                        input{
                            border: none;
                            font-size: 14px;
                            line-height: 32px;
                            padding-top: 0;
                        }
                    }
                }
                
                button[type="submit"]{
                    background: #5EC5FF;
                    box-shadow: 0px 0px 4px 0px #00000040 inset;
                    color: #FFF;
                    font-family: Montserrat;
                    font-weight: 700;
                    font-size: 14px;
                    line-height: 30px;
                    letter-spacing: 0%;
                    border-radius: 16px;
                    margin-left: 10%;
                    margin-right: 10%;
                    width: 80%;
                    text-align: center;
                    margin-top: 20px;
                    cursor: pointer;
                    &:hover{
                        background: #83cff8;
                    }


                }
            }
        }
    }

}
@media only screen and (min-width : $responsive-point) {
    .account{
        width: $responsive-point;
        margin: 0 auto;
        position: relative;
        flex-direction: row;
        
        .account-nav{
            width: 40%;
            padding-left: 20px;
            padding-right: 20px;
            &:before{
                left: calc( ($responsive-point - 100vw) / 2);
                width: calc(100% + (100vw - $responsive-point) /2);
            }
            h1{
                text-align: left;
            }
            nav{
                display: block;
                padding-left: 0px;
                a{
                    float: left;
                    clear: both;
                    text-align: left;
                    padding-left: 0;
                    padding-bottom: 5px;
                    font-size: 18px;
                }
            }
        }
        .content{
            width: 60%;
            padding: 20px;
        }
    }
}
</style>

<route lang="yaml">
    name: account.lost
    meta:
        title: J'ai oublié mon mot de passe - Kase
        layout: default
        middleware: guest
</route>